import { formatNumber } from "@/v2-common/utils/utils.formatting"
import { DATA_VIZ } from "@/v2-ui/color/dataViz.colors"
import Link from "@/v2-backoffice-client/components/link"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import { buildCustomerMcpLink } from "@/v2-backoffice-client/portfolio/portfolio.utils"
import { list } from "@/v2-backoffice-client/usage/usage.text"
import type { PortfolioMcpConcentration as Concentration } from "@/v2-backoffice-client/portfolio/portfolio.mcp.utils"

export type PortfolioMcpConcentrationProps = {
  concentration: Concentration
}

/** Slice colours, biggest customer first; the rest is neutral. */
const SLICE_COLORS = [
  DATA_VIZ.brand.greenBright,
  DATA_VIZ.brand.green2,
  DATA_VIZ.brand.green3,
  DATA_VIZ.brand.greenDeep,
]

// "Where the volume sits" as a picture: one bar of the last 28 days' calls
// cut into the top customers and everyone else, the headline share in
// numbers, and a legend row per slice. The sentence this replaces named
// four customers and a percentage; the bar shows the same at a glance.
function PortfolioMcpConcentration(props: PortfolioMcpConcentrationProps) {
  const { concentration } = props
  const { top, rest, topShare, calls28d, quiet } = concentration
  const customerCount = top.length + rest.count

  if (top.length === 0) return null

  return (
    <UsageReportCard
      title="Where the volume sits"
      caption={
        quiet.length > 0
          ? `${formatNumber(quiet.length)} ${quiet.length === 1 ? "customer has" : "customers have"} used it before but not in the last four weeks${
              quiet.length <= 4 ? `: ${list(quiet)}` : ""
            }.`
          : undefined
      }
    >
      <p className="text-sm">
        <span className="text-2xl font-semibold tabular-nums">
          {Math.round(topShare * 100)} %
        </span>{" "}
        <span className="text-muted-foreground">
          of the last 28 days&apos; {formatNumber(calls28d)} calls come from{" "}
          {top.length} of {formatNumber(customerCount)} customers
        </span>
      </p>
      <div
        className="flex h-3 w-full overflow-hidden rounded-full bg-muted"
        role="img"
        aria-label="Share of the last 28 days' calls per customer"
      >
        {top.map((customer, index) => (
          <span
            key={customer.externalCustomerId}
            className="h-full"
            style={{
              width: `${customer.share * 100}%`,
              backgroundColor: SLICE_COLORS[index % SLICE_COLORS.length],
            }}
            title={`${customer.name}: ${formatNumber(customer.calls)} calls`}
          />
        ))}
      </div>
      <ul className="flex flex-col gap-1 text-xs">
        {top.map((customer, index) => (
          <li
            key={customer.externalCustomerId}
            className="flex items-center gap-2"
          >
            <span
              aria-hidden
              className="size-2 shrink-0 rounded-full"
              style={{
                backgroundColor: SLICE_COLORS[index % SLICE_COLORS.length],
              }}
            />
            <Link
              to={buildCustomerMcpLink(customer.externalCustomerId)}
              iconSrc={null}
              className="min-w-0 flex-1 truncate"
            >
              {customer.name}
            </Link>
            <span className="w-10 text-right tabular-nums text-muted-foreground">
              {Math.round(customer.share * 100)} %
            </span>
            <span className="w-16 text-right tabular-nums">
              {formatNumber(customer.calls)}
            </span>
          </li>
        ))}
        {rest.count > 0 && (
          <li className="flex items-center gap-2 text-muted-foreground">
            <span
              aria-hidden
              className="size-2 shrink-0 rounded-full bg-muted-foreground/40"
            />
            <span className="min-w-0 flex-1 truncate">
              {formatNumber(rest.count)} other{" "}
              {rest.count === 1 ? "customer" : "customers"}
            </span>
            <span className="w-10 text-right tabular-nums">
              {Math.round(rest.share * 100)} %
            </span>
            <span className="w-16 text-right tabular-nums">
              {formatNumber(rest.calls)}
            </span>
          </li>
        )}
      </ul>
    </UsageReportCard>
  )
}

export default PortfolioMcpConcentration
